// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FlexWrapper from "@store/Library/Components/Toolkit/FlexWrapper";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { css } from "styled-components";

const enum fields {
    gridVariant = "gridVariant",
    totalGridHeights = "totalGridHeights",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gridVariant]: string;
        [fields.totalGridHeights]: number;
    };
}

type Props = OwnProps;

const contentGapValue = 30;

export interface TilesGridStyles {
    flexWrapper?: FlexWrapperPresentationProps;
}

export const bannersGridStyles: TilesGridStyles = {
    flexWrapper: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${spacingStyles(pxToRem(contentGapValue), pxToRem(contentGapValue))}
                `,
            },
            content: {
                css: css``,
            },
            item: {
                css: css``,
            },
        },
    },
};

const styles = bannersGridStyles;

const TilesGrid = ({ fields: { gridVariant, totalGridHeights }, id }: Props) => {
    const { isInShell } = useShellContext();
    const oneOneGrid = gridVariant === "oneOneGrid";
    const oneTwoGrid = gridVariant === "oneTwoGrid";
    const twoOneGrid = gridVariant === "twoOneGrid";
    const twoTwoGrid = gridVariant === "twoTwoGrid";
    const twoColumnsGrid = oneOneGrid || oneTwoGrid || twoOneGrid || twoTwoGrid;

    const gridItemMaxHeight = `${totalGridHeights}vh`;

    const wrapperStyles = {
        verticapGap: pxToRem(contentGapValue),
        extendedStyles: {
            wrapper: {
                css: css`
                    ${styles.flexWrapper?.extendedStyles?.wrapper?.css}

                    ${isInShell &&
                    `
                        &:last-child {
                            margin-bottom: ${pxToRem(40)};
                        }
                    `}
                `,
            },
            content: {
                css: css`
                    ${styles.flexWrapper?.extendedStyles?.content?.css}
                    gap: ${pxToRem(contentGapValue)};
                    ${isInShell
                        ? `
                            ${
                                twoTwoGrid
                                    ? `
                                height: 100%;
                            `
                                    : `
                                height: calc(${gridItemMaxHeight} + 100px);
                            `
                            }
                        `
                        : breakpointValues(
                              [null, null, gridItemMaxHeight],
                              oneOneGrid || twoTwoGrid ? "min-height" : "height",
                          )}
                    ${oneTwoGrid || twoOneGrid ? breakpointValues(["column", null, "column wrap"], "flex-flow") : ""}
                `,
            },
            item: {
                css: css`
                    ${styles.flexWrapper?.extendedStyles?.item?.css}
                    ${oneTwoGrid
                        ? `
                            :first-child {
                                height: 100%;
                            }

                            :nth-child(2),
                            :nth-child(3) {
                                height: calc(50% - ${pxToRem(contentGapValue / 2)});
                            }
                        `
                        : ""}
                    ${twoOneGrid
                        ? `
                            :first-child,
                            :nth-child(2) {
                                height: calc(50% - ${pxToRem(contentGapValue / 2)});
                            }
                            :nth-child(3) {
                                height: 100%;
                            }
                        `
                        : ""}
                    ${twoColumnsGrid
                        ? breakpointValues(["100%", null, `calc(50% - ${contentGapValue / 2}px)`], "width")
                        : ""}
                    ${isInShell &&
                    `
                        [data-widget="Basic/Banner"],
                        [data-widget="Basic/Slideshow"] {
                            height: 100%;
                        }

                        /* To adjust the visual on Edit Mode */
                        [class*="ZoneWrapper-"] {
                            display: flex;
                            flex-direction: column;
                            padding: 0;
                            position: relative;

                            > [data-widget="Basic/Banner"],
                            > [data-widget="Basic/Slideshow"] {
                                height: 100%;

                                > [class*="WidgetStyle-"] {
                                    height: 100%;
                                    padding: 0;

                                    > [class*="HoverStyle-"] {
                                        z-index: 100;
                                    }
                                    
                                    > [class*="WidgetWrapper-"] {
                                        height: 100%;
                                    }
                                }
                            }

                            > [data-widget="Basic/Banner"] + [class*="AddContainer-"],
                            > [data-widget="Basic/Slideshow"] + [class*="AddContainer-"] {
                                display: none;
                            }
                        }
                    `}
                    
                    /* To have slide-indicator in slideshow higher when in Tiles */
                    .slide-indicator {
                        ${spacingStyles(pxToRem(5), null)}
                    }
                `,
            },
        },
    };

    let gridItems;
    switch (gridVariant) {
        case "oneOneGrid":
            gridItems = ["Zone1", "Zone2"];
            break;
        case "oneTwoGrid":
        case "twoOneGrid":
            gridItems = ["Zone1", "Zone2", "Zone3"];
            break;
        case "twoTwoGrid":
            gridItems = ["Zone1", "Zone2", "Zone3", "Zone4"];
            break;
    }

    return (
        <FlexWrapper {...wrapperStyles}>
            {gridItems?.map(item => {
                return <Zone contentId={id} zoneName={item} key={item} />;
            })}
        </FlexWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TilesGrid,
    definition: {
        group: "Common",
        displayName: "HomePage Tiles Grid",
        icon: "grid",
        fieldDefinitions: [
            {
                name: fields.gridVariant,
                displayName: "Grid Variant",
                editorTemplate: "RadioButtonsField",
                fieldType: "General",
                defaultValue: "1",
                options: [
                    {
                        displayName: "One-One Grid",
                        value: "oneOneGrid",
                    },
                    {
                        displayName: "One-Two Grid",
                        value: "oneTwoGrid",
                    },
                    {
                        displayName: "Two-One Grid",
                        value: "twoOneGrid",
                    },
                    {
                        displayName: "Two-Two Grid",
                        value: "twoTwoGrid",
                    },
                ],
            },
            {
                name: fields.totalGridHeights,
                displayName: "Total Grid Height (in %)",
                tooltip: "Relative to viewport height.",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 50,
                min: 10,
                max: 100,
            },
        ],
    },
};

export default widgetModule;
